import { useState, useEffect } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { authApi } from '@/lib/api/services/auth';
import { toast } from '@/hooks/use-toast';
const we_next_logo = '/assets/wenext_logo_dark.png';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token. Please request a new password reset.');
        }
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validation
        if (!token) {
            setError('Invalid reset token');
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }


        try {
            const result = await authApi.resetPassword({ token, password });

            if (result.error) {
                setError(result.error);
            } else {
                setSuccess(true);
                toast({ variant: "success",
                    title: "Password reset!",
                    description: "Your password has been reset successfully.",
                });
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/auth');
                }, 2000);
            }
        } catch (error: any) {
            setError(friendlyMessage(error, 'Failed to reset password'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex flex-col">
            {/* Header */}
            <header className="p-4 sm:p-6 flex items-center justify-between">
                <div className="flex items-center">
                    <img
                        src={we_next_logo}
                        alt="WeNext Logo"
                        className="w-[200px] sm:w-[200px] min-w-[200px]"
                    />
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl">Reset Password</CardTitle>
                        <CardDescription>
                            Enter your new password below
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {success ? (
                            <div className="space-y-4">
                                <div className="flex flex-col items-center justify-center space-y-4 py-4">
                                    <CheckCircle2 className="h-12 w-12 text-green-500" />
                                    <div className="text-center">
                                        <h3 className="text-lg font-semibold mb-2">Password Reset Successful</h3>
                                        <p className="text-sm text-muted-foreground">
                                            Your password has been reset. Redirecting to sign in...
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => navigate('/auth')}
                                    className="w-full"
                                >
                                    Go to Sign In
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}

                                {!token && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            Invalid or missing reset token. Please{' '}
                                            <Link to="/forgot-password" className="underline font-semibold">
                                                request a new password reset
                                            </Link>
                                            .
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="password">New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Enter your new password"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                // Clear error when user types
                                                if (error && !error.includes('token')) {
                                                    setError('');
                                                }
                                            }}
                                            required
                                            minLength={6}
                                            className="pr-10"
                                            disabled={!token || loading}
                                            autoFocus={!!token}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                            onClick={() => setShowPassword(!showPassword)}
                                            disabled={!token || loading}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-muted-foreground" />
                                            )}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Password must be at least 6 characters long
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="Confirm your new password"
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                // Clear error when user types
                                                if (error && error.includes('Passwords do not match')) {
                                                    setError('');
                                                }
                                            }}
                                            required
                                            minLength={6}
                                            className="pr-10"
                                            disabled={!token || loading}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            disabled={!token || loading}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-muted-foreground" />
                                            )}
                                        </Button>
                                    </div>
                                    {password && confirmPassword && password !== confirmPassword && (
                                        <p className="text-xs text-destructive">
                                            Passwords do not match
                                        </p>
                                    )}
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={loading || !token || !password || !confirmPassword}
                                >
                                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {loading ? 'Resetting Password...' : 'Reset Password'}
                                </Button>

                                <div className="text-center">
                                    <Button
                                        type="button"
                                        variant="link"
                                        onClick={() => navigate('/auth')}
                                        className="text-sm text-muted-foreground hover:text-primary"
                                        disabled={loading}
                                    >
                                        <ArrowLeft className="mr-1 h-3 w-3" />
                                        Back to Sign In
                                    </Button>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Footer */}
            <footer className="p-4 sm:p-6 border-t text-center text-xs sm:text-sm text-muted-foreground">
                <div className="space-y-2">
                    <p>© 2025 WeNext. Powered by PhotonX</p>
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0 sm:space-x-4">
                        <Link
                            to="/privacy-policy"
                            className="hover:text-primary transition-colors underline"
                        >
                            Privacy Policy
                        </Link>
                        <span className="hidden sm:inline">•</span>
                        <Link
                            to="/terms-of-service"
                            className="hover:text-primary transition-colors underline"
                        >
                            Terms of Service
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}

